import { useEffect, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { disableNetwork, enableNetwork } from "firebase/firestore";
import { db, logException } from "@um/ui";

/**
 * Reconnect Firestore when the app returns to the foreground (UM-61).
 *
 * When the OS backgrounds the app it freezes the WebView and drops the
 * network; Firestore's stream can stall without ever erroring, so onSnapshot
 * listeners silently stop delivering (the error-driven retry wrapper never
 * fires). Cycling disableNetwork/enableNetwork forces every active listener
 * to reconnect and re-deliver current data.
 *
 * Capacitor's appStateChange fires on native app switching and, via its web
 * implementation, on document visibility changes — so this covers PWA too.
 */
export function useForegroundFirestoreReconnect() {
  const cyclingRef = useRef(false);

  useEffect(() => {
    const listenerPromise = CapacitorApp.addListener(
      "appStateChange",
      ({ isActive }) => {
        // appStateChange only fires on transitions, so isActive means we just
        // came back to the foreground.
        if (!isActive || cyclingRef.current) return;
        cyclingRef.current = true;
        void (async () => {
          try {
            await disableNetwork(db);
            await enableNetwork(db);
          } catch (e) {
            logException(e);
          } finally {
            cyclingRef.current = false;
          }
        })();
      }
    );

    return () => {
      listenerPromise.then((handle) => handle.remove());
    };
  }, []);
}
